/* eslint-disable no-case-declarations */
import { LitElement, html, property } from 'lit-element';
import { FireMixin } from '@forter/mixins/fire-mixin';

import style from './fc-popover.css';
import { classMap } from 'lit-html/directives/class-map';
import { WindowResizeMixin } from '@forter/mixins/window-resize-mixin';

export const POSITIONS = {
  RIGHT: 'right',
  LEFT: 'left',
  TOP: 'top',
  BOTTOM: 'bottom',
  BOTTOM_LEFT: 'bottom_left',
  BOTTOM_RIGHT: 'bottom_right',
  RIGHT_TOP: 'right_top',
  LEFT_TOP: 'left_top',
};


const getFixedPosition = elem => {
  if (!elem) return { top: 0, left: 0 };
  const rect = elem.getBoundingClientRect();
  // const win = elem.ownerDocument.defaultView;
  return {
    top: rect.top,
    left: rect.left,
  };
};

/**
 * An element to be used to show additional content or operations
 * <!-- Author: Ori Weingart <oriweingart@forter.com> -->
 *
 * ## Usage
 *
 * ```html
 * <script>
 *   import '@forter/popover';
 * </script>
 *
 * <fc-popover>
 *     <fc-button slot="toggle">Open</fc-button>
 *     <div slot="content">
 *         <div>This is the content</div>
 *         <div>Of the famous popover</div>
 *         <div>From forter web components</div>
 *         <div>...</div>
 *     </div>
 * </fc-popover>
 * ```
 *
 * ## Examples
 *
 *
 * <!-- With PopoverItem -->
 * <fc-popover  position="right_top" closeable>
 *  <fc-button slot="toggle">Open</fc-button>
 *  <div slot="content">
 *     <fc-popover-item tabindex="1">
 *       <fc-icon icon="ecommerce"></fc-icon>
 *       <section>Forter protects all of your online transactions from fraud </section>
 *     </fc-popover-item>
*     <fc-popover-item tabindex="2">
 *       <fc-icon icon="inr"></fc-icon>
 *       <section>Forter automatically evaluates the risk of each transaction affected by the directive </section>
 *     </fc-popover-item>
 *    <fc-popover-item tabindex="3" disabled>
 *       <fc-icon icon="loyalty"></fc-icon>
 *       <section> protects your loyalty program accounts from unauthorized access or attempts to steal their points </section>
 *     </fc-popover-item>
 * </div>
 </fc-popover>
 *
 * <!-- complex -->
 * <fc-popover  position="right_top" closeable>
 *  <fc-button slot="toggle">Open</fc-button>
 *  <fc-list slot="content">
 *     <fc-list-item>
 *       <fc-icon icon="ecommerce"></fc-icon>
 *       <label> Ecommerce </label>
 *       <section>Forter protects all of your online transactions from fraud </section>
 *     </fc-list-item>
 *     <fc-list-item>
 *         <fc-icon icon="inr"></fc-icon>
 *         <label> INR Solution </label>
 *         <section>Forter automatically evaluates the risk of each transaction affected by the directive </section>
 *     </fc-list-item>
 *     <fc-list-item>
 *         <fc-icon icon="loyalty"></fc-icon>
 *         <label> Loyalty </label>
 *         <section> protects your loyalty program accounts from unauthorized access or attempts to steal their points </section>
 *     </fc-list-item>
 *     <fc-list-item>
 *       <fc-icon icon="returns"></fc-icon>
 *       <label> Returns </label>
 *       <section>Identify and block consumers who abuse your business’ refund policies. </section>
 *     </fc-list-item>
 *
 *     <fc-list-item>
 *       <fc-icon icon="promotions"></fc-icon>
 *       <label> Promotions </label>
 *       <section> Prevent financial losses due to users who take advantage of your promotions and coupons.. </section>
 *     </fc-list-item>
 *
 *     <fc-list-item>
 *       <fc-icon icon="gateway"></fc-icon>
 *       <label> Gateway </label>
 *       <section> determine whether credit card applicants are legitimate to safeguard your customer relationships </section>
 *     </fc-list-item>
 *
 *     <fc-list-item>
 *         <fc-icon icon="developer"></fc-icon>
 *         <label>Developer</label>
 *         <section> Integration tools to help faster and smoother integration to Forter.</section>
 *     </fc-list-item>
 * </fc-list>
  </fc-popover>
 * ```
 *
 * @element fc-popover
 * @fires opened - when visibility of pop over change. method: `toggle`, example: `{ "open" : true }`
 * @fires closed - hide popover visibility. method: `close`,
 * example: `{ "open" : false }`
 * @fires transition-end - when the conent finishes fading
 * @cssprop --fc-popover-background-color - content's background color. default: `white`
 * @cssprop --fc-popover-padding - content's padding color. default: `10px`
 * @cssprop --fc-popover-arrow-size - content's arrow size. default: `10px`
 * @cssprop --fc-popover-box-shadow - content's box shadow size. default: `0 2px 6px 2px rgba(183, 203, 206, 0.25)`
 * @cssprop --fc-popover-border-radius - content's border radios. default: `10px`
 * @cssprop --fc-popover-z-index - content's z-index. default: `999`
 */

export class FcPopover extends WindowResizeMixin(FireMixin(LitElement)) {
  /**
   * popover position with relative to toggle slot
   * @type {"top"|"bottom"|"left"|"right"|"right_top"|"left_top"|"bottom_left"|"bottom_right"}
   * @attr
   */
  @property({ type: String, reflect: true }) position = POSITIONS.BOTTOM;

  /**
   * disabled boolean. default: false, example: true
   * @type {Boolean}
   * @attr
   */
   @property({ type: Boolean }) disabled = false;

  /**
   * horizontal offset for the content in pixels
   * @type {Number}
   * @attr
   */
  @property({ type: Number }) horizontaloffset = 0;

  /**
   * vertical offset for the content in pixels
   * @type {Number}
   * @attr
   */
  @property({ type: Number }) verticaloffset = 0;

  /**
   * whether popover is opened.
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, reflect: true }) open = false;

  /**
   * whether popover is opened by mouse over instead of click.
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, reflect: true }) openonhover = false;

  /**
   * whether popover is closed by clicking content.
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, reflect: true }) closeable = false;

  /** @inheritdoc */
  static is = 'fc-popover';

  /** @inheritdoc */
  static styles = [style];

  constructor() {
    super();
    this.toggle = this.toggle.bind(this);
    this.close = this.close.bind(this);
    this.registerToggle = this.registerToggle.bind(this);
    this.leaveToggle = this.leaveToggle.bind(this);
  }

  /**
   * Toggle popover content visibility
   * @param {Event} e - fired event
   * @param {Boolean} openState - (optional) to force the content open / close state
   */
  toggle(e, openState) {
    if (this.disabled) return;
    this.open = openState === true || openState === false ? openState : !this.open;
    this.fire('opened', { open: this.open });
    if (!this.open) {
      this.close();
    }
  }

  /**
   * Set timeout to leave toggle if mouse is not on content
   */
  leaveToggle() {
    setTimeout(() => {
      if (!this.isOnContent) {
        this.open = false;
        this.fire('opened', { open: this.open });
        this.close();
      }
    }, 200);
  }

  /**
   * close popover content visibility
   */
  close() {
    this.open = false;
    this.fire('closed', { open: this.open });
  }

  /**
   * Register toggle even listener on click/hover
   * @param {HTMLElement} toggleElm
   */
  registerToggle({ target: toggleElm }) {
    const contentElm = this.querySelector('[slot="content"]');
    if (this.__registeredToggle) return;
    // on hover
    if (this.openonhover) {
      toggleElm.addEventListener('mouseenter', this.toggle);
      toggleElm.addEventListener('mouseleave', this.leaveToggle);
      contentElm.addEventListener('mouseenter', () => {
        this.isOnContent = true;
      });
      contentElm.addEventListener('mouseleave', () => {
        this.isOnContent = false;
        this.toggle();
      });
    } else {
      // on click
      toggleElm.addEventListener('click', this.toggle);
    }
    if (this.closeable) {
      this.lastElementChild.addEventListener('click', this.toggle);
    }
    this.__registeredToggle = true;
  }

  /** @inheritdoc */
  onWindowResize() {
    // Re-render in case pop over is open while window is resized to reposition the content
    const { open } = this;
    open && this.requestUpdate();
  }

  /**
   * Calculate the content slot position relatively to the toggle element as a fixed x,y position
   * @return {{contentTop: *, contentLeft: *}}
   */
  getContentPosition() {
    const { position } = this;
    let contentTop = 0; let contentLeft = 0;
    const extraDistance = 5;
    const style = getComputedStyle(this);
    const arrowSize = Number.parseInt(style.getPropertyValue('--fc-popover-arrow-size'));
    const paddingSize = Number.parseInt(style.getPropertyValue('--fc-popover-padding'));
    const toggleElm = this.querySelector('[slot="toggle"]');
    const contentElm = this.querySelector('[slot="content"]');
    if (!toggleElm || !contentElm) return { contentTop, contentLeft };
    const { top: toggleTop, left: toggleLeft } = getFixedPosition(toggleElm);
    const { offsetWidth: toggleWidth, offsetHeight: toggleHeight } = toggleElm;
    let { offsetWidth: contentWidth, offsetHeight: contentHeight } = contentElm;
    contentWidth += paddingSize * 2;
    contentHeight += paddingSize * 2;
    const delta = extraDistance + arrowSize;
    switch (position) {
      case POSITIONS.BOTTOM:
        contentTop = toggleTop + toggleHeight + delta;
        contentLeft = (toggleLeft + ((toggleWidth / 2) - (contentWidth / 2)));
        // Following lines will cover a case where the bottom of the popup is below the bottom of the window.
        // In this case the popup will appear upper so the user could see the whole component,
        // and to the left so the user could see the popup menu item.
        const contentClientHeight = contentElm.clientHeight;
        const windowHeight2 = window.innerHeight;
        const diff = contentClientHeight + contentTop + delta * 2 - windowHeight2;
        const LEFT_SHIFT = 64;
        const BOTTOM_SHIFT = 32;

        if (diff > 0) {
          contentTop = contentTop - diff - LEFT_SHIFT;
          contentLeft = contentLeft - BOTTOM_SHIFT;
        }
        break;
      case POSITIONS.BOTTOM_LEFT:
        contentTop = toggleTop + toggleHeight + delta;
        contentLeft = (toggleLeft - contentWidth + (delta * 3));
        break;
      case POSITIONS.BOTTOM_RIGHT:
        contentTop = toggleTop + toggleHeight + delta;
        contentLeft = toggleLeft + toggleWidth + delta;
        break;
      case POSITIONS.TOP:
        contentTop = toggleTop - contentHeight - delta;
        contentLeft = (toggleLeft + ((toggleWidth / 2) - (contentWidth / 2)));
        break;
      case POSITIONS.RIGHT:
        contentTop = toggleTop + ((toggleHeight / 2) - (contentHeight / 2));
        contentLeft = toggleLeft + toggleWidth + delta;
        break;
      case POSITIONS.RIGHT_TOP:
        contentTop = toggleTop;
        contentLeft = toggleLeft + toggleWidth + delta;
        break;
      case POSITIONS.LEFT_TOP:
        contentTop = toggleTop;
        contentLeft = toggleLeft - contentWidth - delta;
        break;
      case POSITIONS.LEFT:
        contentTop = toggleTop + ((toggleHeight / 2) - (contentHeight / 2));
        contentLeft = toggleLeft - contentWidth - delta;
        break;
    }
    const { horizontaloffset, verticaloffset } = this;
    if (horizontaloffset) {
      contentLeft = contentLeft + Number.parseInt(horizontaloffset);
    }
    if (contentTop) {
      contentTop = contentTop + Number.parseInt(verticaloffset);
    }
    // ensure element is inside viewport
    if (contentTop < 0) {
      // Top is out of viewport
      contentTop = 5;
    }
    if ((contentLeft + contentWidth) >
        (window.innerWidth || document.documentElement.clientWidth)) {
      // Right side is out of viewport
      const delta = (window.innerWidth || document.documentElement.clientWidth) -
          (contentLeft + contentWidth);
      contentLeft = contentLeft + delta;
    }
    if ((contentTop + contentHeight) >
        (window.innerHeight || document.documentElement.clientHeight)) {
      // Bottom is out of viewport
      const delta = (window.innerHeight || document.documentElement.clientHeight)
          - (contentTop + contentHeight);
      contentTop = contentTop + delta;
    }
    if (contentLeft < 0) {
      // Left side is out of viewport
      contentLeft = 5;
    }

    return {
      contentTop: `${contentTop}px`,
      contentLeft: `${contentLeft}px`,
    };
  }

  transitionEnd(event) {
    const { propertyName } = event;
    if (propertyName === 'opacity') {
      this.fire('transition-end');
    }
  }

  getPopoverOrigin(contentTop, contentLeft) {
    let contentOriginX = 0; let contentOriginY = 0;
    contentTop = parseInt(contentTop, 10);
    contentLeft = parseInt(contentLeft, 10);
    const toggleElm = this.querySelector('[slot="toggle"]');
    const content = this.shadowRoot.querySelector('slot[name=content]');
    if (!content || !toggleElm) return { x: contentOriginX, y: contentOriginY };
    const { top: toggleTop, left: toggleLeft } = getFixedPosition(toggleElm);
    const { offsetWidth: toggleWidth, offsetHeight: toggleHeight } = toggleElm;
    const toggleCenter = {
      x: toggleLeft + toggleWidth / 2,
      y: toggleTop + toggleHeight / 2
    };
    contentOriginX = toggleCenter.x - contentLeft;
    contentOriginY = toggleCenter.y - contentTop;
    const { offsetWidth: contentWidth, offsetHeight: contentHeight } = content;
    contentOriginX = Math.max(0, contentOriginX);
    contentOriginX = Math.min(contentWidth, contentOriginX);
    contentOriginY = Math.max(0, contentOriginY);
    contentOriginY = Math.min(contentHeight, contentOriginY);
    return { x: contentOriginX, y: contentOriginY };
  }

  updated() {
    this._firstUpdated = true;
    if (this.interval) {
      clearInterval(this.interval);
      delete this.interval;
    }

    if (this.open) {
      const content = this.shadowRoot.querySelector('slot[name=content]');
      this.interval = setInterval(() => {
        const { contentTop, contentLeft } = this.getContentPosition();
        content.style.top = `var(--fc-popover-top, ${contentTop})`;
        content.style.left = `var(--fc-popover-left, ${contentLeft})`;
        const contentOrigin = this.getPopoverOrigin(contentTop, contentLeft);
        content.style.transformOrigin = `var(--fc-popover-transform-origin, ${contentOrigin.x}px ${contentOrigin.y}px)`;
      }, 100);
    }
  }

  getRenderPosition() {
    if (!this._firstUpdated && !this.open) {
      /**
       * To improve performance: avoid calling getContentPosition which is expensive if component was not rendered and not open yet.
       * In such case we draw the slot in on 0,0 coordinate (it's presented anyway)
       */
      return { x: 0, y: 0, contentLeft: 0, contentTop: 0 };
    }
    const { contentTop, contentLeft } = this.getContentPosition();
    const { x, y } = this.getPopoverOrigin(contentTop, contentLeft);
    return { x, y, contentLeft, contentTop };
  }

  render() {
    const { open, position } = this;
    const { x, y, contentLeft, contentTop } = this.getRenderPosition();
    const overlay = !this.openonhover; // avoid overlay on hover
    return html`
    <div  class="${classMap({ open, overlay })}" 
          @click="${this.close}"></div>
      <slot name="toggle" 
          @slotchange="${this.registerToggle}"></slot>
    <slot @transitionend="${this.transitionEnd}" name="content" 
          class="${classMap({ open, [`arrow-${position}`]: true })}" 
          style="top: var(--fc-popover-top, ${contentTop}); left: var(--fc-popover-left, ${contentLeft}); transform-origin: var(--fc-popover-transform-origin, ${x}px ${y}px); "></slot>`;
  }
}
